/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}

// #CD CODE
var Shopify = Shopify || {};
// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.money_format = "${{amount}}";
Shopify.formatMoney = function(cents, format) {
  if (typeof cents === 'string') { cents = cents.replace('.', ''); }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = (format || this.money_format);

  function defaultOption(opt, def) {
     return (typeof opt === 'undefined' ? def : opt);
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ',');
    decimal   = defaultOption(decimal, '.');

    if (isNaN(number) || number == null) { return 0; }

    number = (number/100.0).toFixed(precision);

    var parts   = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents   = parts[1] ? (decimal + parts[1]) : '';

    return dollars + cents;
  }

  switch(formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
};


// ---------------------------
// Quantity buttons (FIXED)
// ---------------------------
document.addEventListener('click', function (e) {
  if (!e.target.matches('.qtybox .btnqty')) return;

  const btn = e.target;
  const qtyBox = btn.closest('.qtybox');
  const variantQuantity = btn.closest('.variant_quantity');
  const maxQty = parseInt(variantQuantity.dataset.variantQuantity, 10);
  const input = qtyBox.querySelector('.quantity-input');

  let qty = parseInt(input.value, 10) || 0;

  if (btn.classList.contains('qtyplus')) {
    if (qty < maxQty) qty++;
  } else {
    if (qty > 0) qty--;
  }

  input.value = qty;

  // 👉 Actualizar UI completa
  updateStickyState();
  bounceAddToCart();
});





// ---------------------------
// ===============================
// Sticky add-to-cart (COMPLETO)
// ===============================
document.addEventListener('click', function (e) {
  const btn = e.target.closest('.sticky_action_bar .action_btn');
  if (!btn) return;

  e.preventDefault();

  const items = [];

  // Tomar cantidades reales desde las tarjetas
  document.querySelectorAll('.qtybox.variant_quantity').forEach(box => {
    const id = parseInt(box.dataset.varId, 10);
    const qty = parseInt(
      box.querySelector('.quantity-input')?.value || 0,
      10
    );

    if (id && qty > 0) {
      items.push({ id, quantity: qty });
    }
  });

  if (!items.length) return;

  btn.textContent = 'Añadiendo...';
  btn.disabled = true;

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items })
  })
    .then(res => res.json())
    .then(() => fetch('/cart.js'))
    .then(res => res.json())
    .then(cart => {
      console.log('Carrito actualizado', cart);

      // 🔹 Notificar al tema (header / contador)
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'sticky-add', {
          itemCount: cart.item_count
        })
      );

      // 🔹 Abrir drawer
      const drawer =
        document.querySelector('cart-drawer') ||
        document.querySelector('.cart-drawer');

      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      } else {
        const cartBtn =
          document.querySelector('[aria-controls="cart-drawer"]') ||
          document.querySelector('.header__icon--cart') ||
          document.querySelector('a[href="/cart"]');

        if (cartBtn) cartBtn.click();
      }

      // 🔥 Reset completo del estado visual
      resetVariantSelection();

      btn.textContent = 'AGREGADO ✔';

      setTimeout(() => {
        btn.textContent = 'AGREGAR AL CARRITO';
        btn.disabled = false;
      }, 1400);
    })
    .catch(err => {
      console.error('Error carrito:', err);
      btn.textContent = 'Error';
      btn.disabled = false;
    });
});

// ===============================
// Actualiza círculos del sticky
// ===============================
function updateStickyColors() {
  const container = document.querySelector('.selected_colors');
  if (!container) return;

  container.innerHTML = '';

  document.querySelectorAll('.qtybox.variant_quantity').forEach(box => {
    const qty = parseInt(
      box.querySelector('.quantity-input')?.value || 0,
      10
    );
    if (qty <= 0) return;

    const color = box.dataset.bgColor || '#000';
    const textColor = box.dataset.textColor || '#fff';

    // Tomar nombre del color
    const title =
      box.closest('.single_variant_select')
        ?.querySelector('.variant_title span')?.textContent || '';

    const item = document.createElement('div');
    item.className = 'selected_color_item';

    const circle = document.createElement('div');
    circle.className = 'selected_color_circle';
    circle.style.background = color;
    circle.style.color = textColor;
    circle.textContent = qty;

    const name = document.createElement('span');
    name.textContent = title;

    item.appendChild(circle);
    item.appendChild(name);
    container.appendChild(item);
  });

  // Mostrar sticky si hay selección
  const stickyBar = document.querySelector('.sticky_action_bar');
  if (stickyBar && container.children.length) {
    stickyBar.classList.remove('hidden');
  }
}

function updateStickyState() {
  let totalQty = 0;
  let totalPrice = 0;

  document.querySelectorAll('.qtybox.variant_quantity').forEach(box => {
    const qty = parseInt(
      box.querySelector('.quantity-input')?.value || 0,
      10
    );
    if (!qty) return;

    const price = parseInt(box.dataset.price, 10) || 0;
    totalQty += qty;
    totalPrice += qty * price;
  });

  // Totales
  const qtyEl = document.querySelector('.sticky_action_bar .var_pice');
  const priceEl = document.querySelector('.sticky_action_bar .var_price');

  if (qtyEl) qtyEl.textContent = totalQty;
  if (priceEl)
    priceEl.innerHTML = Shopify.formatMoney(
      totalPrice,
      document.body.getAttribute('data-money-format')
    );

  // Sticky show / hide (SIN PARPADEO)
  const stickyBar = document.querySelector('.sticky_action_bar');
  if (!stickyBar) return;

  if (totalQty > 0) {
    stickyBar.classList.remove('hidden');
  } else {
    stickyBar.classList.add('hidden');
  }

  // Círculos
  updateStickyColors();
}



// ===============================
// Rebote del botón
// ===============================
function bounceAddToCart() {
  const btn = document.querySelector('.sticky_action_bar .action_btn');
  if (!btn) return;

  btn.classList.remove('bounce');
  void btn.offsetWidth;
  btn.classList.add('bounce');
}

// ===============================
// Detecta + y -
// ===============================
document.addEventListener('click', e => {
  if (
    e.target.classList.contains('qtyplus') ||
    e.target.classList.contains('qtyminus')
  ) {
    setTimeout(() => {
      updateStickyColors();
      bounceAddToCart();
    }, 40);
  }
});

// ===============================
// Reset total del estado
// ===============================
function resetVariantSelection() {
  // Reset cantidades
  document.querySelectorAll('.qtybox .quantity-input').forEach(input => {
    input.value = 0;
  });

  // Limpiar círculos
  const container = document.querySelector('.selected_colors');
  if (container) container.innerHTML = '';

  // Ocultar sticky si usas hidden
  const stickyBar = document.querySelector('.sticky_action_bar');
  if (stickyBar) stickyBar.classList.add('hidden');

  console.log('Selección de variantes rseteada');
}
